import api from "./api";
import { AxiosInstance } from "axios";
import mockApi from "./mockApi";
import { API_CONFIG } from "../config/apiConfig";
import { ApiResponse, BasicResponse, AddJobSeekerDetailsRequest, GetJobSeekerDetailsResponse, UpdateEmailRequest, User, AddContactDetailsRequest } from "../types/api";
import {
  UserProfileData,
  Application,
  Job,
  JobDetails,
  ApplyFormData,
} from "../types/components";
// Use mock API in development or when specified
const USE_MOCK_API =
  process.env.REACT_APP_USE_MOCK === "true" && API_CONFIG.IS_DEVELOPMENT;
const apiClient = USE_MOCK_API ? (mockApi as any) : (api as AxiosInstance); // TODO fix mockApi type

const userService = {
  // Jobseeker onboarding step submit (generic passthrough)
  onboard: async (payload: Record<string, any>): Promise<ApiResponse<any>> => {
    try {
      const response = await (api as AxiosInstance).post(
        "/Jobseeker/onboard",
        payload
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Get user profile
  getProfile: async (): Promise<ApiResponse<UserProfileData>> => {
    try {
      const response = await (api as AxiosInstance).get("/user/profile");
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Update user profile
  updateProfile: async (
    profileData: UserProfileData
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await (api as AxiosInstance).put(
        "/user/profile",
        profileData
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Get user applications
  getApplications: async (): Promise<ApiResponse<Application[]>> => {
    try {
      const response = await (api as AxiosInstance).get("/user/applications");
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Apply for job
  applyForJob: async (
    jobId: string,
    applicationData: ApplyFormData
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await (api as AxiosInstance).post(
        `/user/jobs/${jobId}/apply`,
        applicationData
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Get available jobs
  getJobs: async (
    filters: Record<string, any> = {}
  ): Promise<ApiResponse<Job[]>> => {
    try {
      const response = await (api as AxiosInstance).get("/user/jobs", {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Get job details
  getJobDetails: async (jobId: string): Promise<ApiResponse<JobDetails>> => {
    try {
      const response = await (api as AxiosInstance).get(`/user/jobs/${jobId}`);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  // Upload resume
  uploadResume: async (file: File): Promise<ApiResponse<any>> => {
    try {
      const formData = new FormData();
      formData.append("resume", file);
      const response = await (api as AxiosInstance).post(
        "/user/upload-resume",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },
    
    //contact Us 
    addContactusDetail: async (formValues: AddContactDetailsRequest) => {
    try {
      const response = await apiClient.post(
        "/api/Contact/submitContactUs",
        formValues
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  addJobSeekerDetails: async (details: AddJobSeekerDetailsRequest): Promise<BasicResponse<any>> => {
    try {
      const response = await (api as AxiosInstance).put('/api/JobSeeker/addDetails', details);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  getJobSeekerDetails: async (userId: string): Promise<BasicResponse<GetJobSeekerDetailsResponse>> => {
    try {
      const response = await (api as AxiosInstance).get(`/api/JobSeeker/details/${userId}`);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  deleteUser: async (userId: string): Promise<BasicResponse<boolean>> => {
    try {
      const response = await (api as AxiosInstance).delete('/api/User/deleteUser', { params: { userId } });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  },

  updateUserEmail: async (emailRequest: UpdateEmailRequest): Promise<BasicResponse<string>> => {
    try {
      const response = await (api as AxiosInstance).put('/api/User/email', emailRequest);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error.message;
    }
  }
};

export default userService;
